import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { IconComponent, Modal } from '@softline/ui-core';

@Component({
  selector: 'lib-image-carousel-dialog',
  imports: [IconComponent],
  templateUrl: './image-carousel-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./image-carousel-dialog.component.css'],
})
export class ImageCarouselDialogComponent implements Modal<unknown> {
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly imageList = signal<File[]>([]);
  protected readonly index = signal(0);

  set images(value: File[] | null | undefined) {
    this.imageList.set(value ?? []);
    this.index.update((i) => this.clamp(i));
  }

  set selectedIndex(value: number | null | undefined) {
    this.index.set(this.clamp(value ?? 0));
  }

  readonly hasPrevious = computed(() => this.index() > 0);
  readonly hasNext = computed(() => this.index() < this.imageList().length - 1);
  readonly selectedImage = computed(() => this.imageList()[this.index()] ?? null);

  private readonly objectUrl = computed(() => {
    const image = this.selectedImage();
    return image ? URL.createObjectURL(image) : null;
  });

  readonly selectedImageURL = computed<SafeUrl | null>(() => {
    const url = this.objectUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  close!: (result: unknown) => void;

  constructor() {
    let previousUrl: string | null = null;
    effect(() => {
      const url = this.objectUrl();
      if (previousUrl && previousUrl !== url) URL.revokeObjectURL(previousUrl);
      previousUrl = url;
    });
    inject(DestroyRef).onDestroy(() => {
      if (previousUrl) URL.revokeObjectURL(previousUrl);
    });
  }

  registerCloseHandler(handler: (result: unknown) => void) {
    this.close = handler;
  }

  select(index: number): void {
    this.index.set(this.clamp(index));
  }

  showNext(): void {
    if (this.hasNext()) this.index.update((i) => i + 1);
  }

  showPrevious(): void {
    if (this.hasPrevious()) this.index.update((i) => i - 1);
  }

  private clamp(index: number): number {
    const last = this.imageList().length - 1;
    return Math.min(Math.max(index, 0), Math.max(last, 0));
  }
}
